import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

import { API_URL } from '../config/api';

const EntityContext = createContext(null);

export function EntityProvider({ children }) {
  const { user } = useAuth();
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchEntities = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/entities`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEntities(data);
        if (data.length > 0 && !selectedEntity) {
          setSelectedEntity(data[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch entities:', error);
    } finally {
      setLoading(false);
    }
  }, [user, selectedEntity]);

  useEffect(() => {
    fetchEntities();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const createEntity = async (entityData) => {
    try {
      const response = await fetch(`${API_URL}/api/entities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(entityData)
      });
      if (response.ok) {
        const newEntity = await response.json();
        setEntities(prev => [...prev, newEntity]);
        return newEntity;
      } else {
        const err = await response.text();
        console.error('Create entity failed:', response.status, err);
      }
    } catch (error) {
      console.error('Failed to create entity:', error);
    }
    return null;
  };

  const updateEntity = async (entityId, entityData) => {
    try {
      const response = await fetch(`${API_URL}/api/entities/${entityId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(entityData)
      });
      if (response.ok) {
        const updatedEntity = await response.json();
        setEntities(prev => prev.map(e => e.entity_id === entityId ? updatedEntity : e));
        if (selectedEntity?.entity_id === entityId) {
          setSelectedEntity(updatedEntity);
        }
        return updatedEntity;
      } else {
        const err = await response.text();
        console.error('Update entity failed:', response.status, err);
      }
    } catch (error) {
      console.error('Failed to update entity:', error);
    }
    return null;
  };

  const deleteEntity = async (entityId) => {
    try {
      const response = await fetch(`${API_URL}/api/entities/${entityId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setEntities(prev => prev.filter(e => e.entity_id !== entityId));
        if (selectedEntity?.entity_id === entityId) {
          setSelectedEntity(entities.find(e => e.entity_id !== entityId) || null);
        }
        return true;
      }
    } catch (error) {
      console.error('Failed to delete entity:', error);
    }
    return false;
  };

  // Helper to check if entity is Dutch
  const isDutchEntity = selectedEntity?.country === 'NL';
  const isUSEntity = selectedEntity?.country === 'US';

  return (
    <EntityContext.Provider value={{
      entities,
      selectedEntity,
      setSelectedEntity,
      loading,
      fetchEntities,
      createEntity,
      updateEntity,
      deleteEntity,
      isDutchEntity,
      isUSEntity
    }}>
      {children}
    </EntityContext.Provider>
  );
}

export function useEntity() {
  const context = useContext(EntityContext);
  if (!context) {
    throw new Error('useEntity must be used within an EntityProvider');
  }
  return context;
}
